import React, { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useSite } from "@/context/SiteContext";

const CONSENT_KEY = "suntec_ga_consent"; // "granted" | "denied"

/**
 * GA4 loader + page-view tracker + GDPR consent banner.
 *
 * Behaviour
 * ─────────
 *  · Reads Measurement ID + enabled flag from CMS (`analytics.ga_measurement_id`,
 *    `analytics.enabled`) — editable without redeploy.
 *  · Never fires on `/admin` routes.
 *  · Shows the consent banner only when no choice has been persisted yet.
 *  · On "granted" — injects the gtag.js snippet and tracks every route change.
 *  · On "denied" — nothing is loaded; a discrete "Preferințe cookies" button
 *    (rendered in the banner's absence via `window.__openCookieBanner`) lets the
 *    user re-open the banner from the footer if needed.
 */
export function Analytics() {
  const { t, loaded } = useSite();
  const { pathname } = useLocation();

  const gaId = (t("analytics.ga_measurement_id") || "").trim();
  const enabled = (t("analytics.enabled") || "yes").toLowerCase() !== "no";
  const isAdmin = pathname.startsWith("/admin");
  const active = loaded && enabled && !!gaId && !isAdmin;

  const [consent, setConsent] = useState(() => {
    if (typeof window === "undefined") return null;
    return window.localStorage.getItem(CONSENT_KEY);
  });
  const injectedIdRef = useRef(null); // Guard against double-injection / id change.

  // Inject gtag once consent is granted and a Measurement ID exists.
  useEffect(() => {
    if (!active || consent !== "granted") return;
    if (injectedIdRef.current === gaId) return;

    // If a previous ID was injected, remove its script (allows CMS ID swap).
    const prev = document.querySelector('script[data-suntec-ga="1"]');
    if (prev) prev.remove();
    delete window.gtag;
    delete window.dataLayer;

    const s = document.createElement("script");
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
    s.setAttribute("data-suntec-ga", "1");
    document.head.appendChild(s);

    window.dataLayer = window.dataLayer || [];
    function gtag() { window.dataLayer.push(arguments); }
    window.gtag = gtag;
    gtag("js", new Date());
    gtag("config", gaId, { anonymize_ip: true, send_page_view: false });
    injectedIdRef.current = gaId;
  }, [active, consent, gaId]);

  // Track page views on route change (SPA).
  useEffect(() => {
    if (!active || consent !== "granted" || !window.gtag) return;
    window.gtag("event", "page_view", {
      page_path: pathname,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [pathname, active, consent]);

  // Expose a re-open hook for the footer link.
  useEffect(() => {
    window.__openCookieBanner = () => setConsent(null);
    return () => { delete window.__openCookieBanner; };
  }, []);

  const persist = (choice) => {
    try { window.localStorage.setItem(CONSENT_KEY, choice); } catch (_) {}
    setConsent(choice);
  };

  // Nothing to show if analytics is disabled or we're on /admin.
  if (!enabled || isAdmin || !gaId) return null;
  if (consent === "granted" || consent === "denied") return null;

  return <ConsentBanner onAccept={() => persist("granted")} onDecline={() => persist("denied")} />;
}

function ConsentBanner({ onAccept, onDecline }) {
  const { t } = useSite();
  return (
    <div
      role="dialog"
      aria-live="polite"
      data-testid="cookie-banner"
      className="fixed bottom-4 left-4 right-4 md:left-6 md:right-auto md:bottom-6 md:max-w-md z-[60] bg-white border border-black shadow-[6px_6px_0_0_rgba(0,0,0,1)]"
    >
      <div className="p-5 md:p-6">
        <div className="font-mono-label text-black/60 mb-2">[ COOKIES ]</div>
        <div className="font-display font-medium uppercase text-2xl tracking-tighter leading-[0.95]">
          {t("cookie.banner.title", "Folosim cookie-uri")}
        </div>
        <p className="mt-3 text-sm text-black/80 leading-relaxed">
          {t("cookie.banner.text", "Folosim cookie-uri de analytics pentru a înțelege cum este utilizat site-ul.")}
        </p>
        <div className="mt-5 flex flex-wrap gap-2">
          <button
            type="button"
            data-testid="cookie-accept"
            onClick={onAccept}
            className="inline-flex items-center gap-2 bg-black text-white px-5 py-3 text-sm font-mono uppercase tracking-wider hover:bg-black/85 transition-colors"
          >
            {t("cookie.banner.accept", "Accept")}
          </button>
          <button
            type="button"
            data-testid="cookie-decline"
            onClick={onDecline}
            className="inline-flex items-center gap-2 border border-black px-5 py-3 text-sm font-mono uppercase tracking-wider hover:bg-black hover:text-white transition-colors"
          >
            {t("cookie.banner.decline", "Refuz")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default Analytics;
